// Left sidebar — user info + past query history for this user.
// History loads from /api/history on mount and refreshes when a new answer
// lands. Clicking a history entry fires onSelectHistory, which the parent
// uses to hydrate the full audit into the Chat view via restoredTurns.
//
// Conversation threading (2026-09-30): rows that share a conversation_id are shown as
// ONE entry, named by the thread's first question and counting its turns. Rows logged
// before the client sent conversation ids have none and stay single entries.

import { useEffect, useState } from 'react'
import { fetchHistory } from '../lib/api'
import type { HistoryEntry } from '../lib/types'
import { useAuth } from '../lib/auth'

// Views reachable from the sidebar. 'admin' stays gated on isAdmin; Chat and Morning
// Brief are for everyone — every AE and PS needs the brief, not just admins, so the nav
// block itself can no longer live inside the isAdmin guard.
export type MainView = 'chat' | 'brief' | 'scorecards' | 'me' | 'admin'

interface SidebarProps {
  onSelectHistory?: (entry: HistoryEntry) => void
  refreshKey: number   // bump this from parent to force reload
  loadingRestore?: boolean
  // Admin mode: gates the feedback moderation entry point only.
  isAdmin?: boolean
  // Sales management (MANAGER_USERS): gates the first-demo scorecards view (2026-10-28).
  isManager?: boolean
  currentView?: MainView
  onSwitchView?: (v: MainView) => void
}

function navItems(isAdmin?: boolean, isManager?: boolean): Array<{ view: MainView; label: string; title?: string }> {
  return [
    { view: 'chat', label: '💬 Chat' },
    { view: 'brief', label: "📋 Today's demos", title: "Your demo prep cards for today" },
    ...(isManager
      ? [{
          view: 'scorecards' as MainView,
          label: '📝 Demo scorecards',
          title: 'Every first demo scored on the AE demo rubric — sales management only',
        }]
      : []),
    { view: 'me', label: '🧠 My context', title: 'What Marker remembers for you, your saved workflows, your attachments' },
    ...(isAdmin
      ? [{
          view: 'admin' as MainView,
          label: '🛠️ Feedback review',
          title: 'Review user-submitted feedback awaiting moderation',
        }]
      : []),
  ]
}

// One sidebar entry: a whole conversation, or a lone pre-threading row.
interface HistoryThread {
  key: string                    // conversation_id, or audit_id when there is none
  first: HistoryEntry            // earliest turn — its question names the thread
  latest: HistoryEntry           // most recent turn — orders the list, carries the error flag
  n: number
}

// Rows arrive newest first. Threads keep that order (by most recent activity) because
// a thread is first seen at its newest row; the rows that follow for the same key are
// older, so the last one assigned to `first` is the thread's opening question.
function groupThreads(rows: HistoryEntry[]): HistoryThread[] {
  const byKey = new Map<string, HistoryThread>()
  for (const r of rows) {
    const key = r.conversation_id ?? r.audit_id
    const t = byKey.get(key)
    if (!t) byKey.set(key, { key, first: r, latest: r, n: 1 })
    else { t.n += 1; t.first = r }
  }
  return [...byKey.values()]
}

export function Sidebar({
  onSelectHistory,
  refreshKey,
  loadingRestore,
  isAdmin,
  isManager,
  currentView,
  onSwitchView,
}: SidebarProps) {
  const { user, signOut } = useAuth()
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Track the currently-selected thread so we can visually mark it and
  // show a spinner while the parent hydrates the full record.
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    // Bumping refreshKey means a new turn just landed; clear the "viewing
    // history" selection so the sidebar doesn't keep highlighting an
    // unrelated audit.
    if (refreshKey > 0) setSelectedKey(null)
    // 60 rows, not 30: a thread of eight turns is eight rows, and the list should still
    // reach back a useful distance once people hold real conversations.
    fetchHistory(60)
      .then((r) => {
        if (!cancelled) setHistory(r.history)
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [refreshKey])

  return (
    <div className="w-72 h-full flex flex-col bg-marker-panel2 border-r border-marker-border">
      <div className="p-4 border-b border-marker-border">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              className="w-9 h-9 rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-9 h-9 rounded-full bg-marker-accent flex items-center justify-center text-white font-semibold">
              {(user?.email || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-marker-text truncate">
              {user?.name || user?.email}
            </div>
            <div className="text-xs text-marker-muted truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={signOut}
          className="mt-3 w-full text-xs text-marker-muted hover:text-marker-text text-left"
        >
          Sign out
        </button>
      </div>

      <div className="p-2 border-b border-marker-border space-y-1">
        {navItems(isAdmin, isManager).map(({ view, label, title }) => (
          <button
            key={view}
            onClick={() => onSwitchView?.(view)}
            title={title}
            className={
              'w-full text-left px-2 py-1.5 rounded text-sm transition-colors ' +
              (currentView === view
                ? 'bg-marker-accent/15 text-marker-text ring-1 ring-marker-accent/40'
                : 'text-marker-muted hover:bg-marker-panel hover:text-marker-text')
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        <div className="text-xs text-marker-muted uppercase tracking-wide px-2 pt-2 pb-1">
          Recent questions
        </div>
        {loading && <div className="p-2 text-xs text-marker-muted">Loading...</div>}
        {error && <div className="p-2 text-xs text-marker-bad">{error}</div>}
        {!loading && !error && history.length === 0 && (
          <div className="p-2 text-xs text-marker-muted">No previous questions yet.</div>
        )}
        {groupThreads(history).map((t) => {
          const isSelected = selectedKey === t.key
          const isLoadingThis = isSelected && loadingRestore
          const isThread = t.n > 1
          return (
            <button
              key={t.key}
              onClick={() => {
                setSelectedKey(t.key)
                // The entry carries the conversation_id (or none); the parent decides
                // whether to restore a whole thread or a single turn from it.
                onSelectHistory?.(t.first)
              }}
              className={
                "w-full text-left p-2 rounded text-sm cursor-pointer transition-colors " +
                (isSelected
                  ? "bg-marker-accent/15 text-marker-text ring-1 ring-marker-accent/40"
                  : "text-marker-text hover:bg-marker-panel")
              }
              title={(isThread ? 'Reopen this conversation' : 'Open past question') + ` · ${t.latest.created_at}`}
            >
              <div className="truncate flex items-center gap-2">
                {isLoadingThis && (
                  <span className="inline-block w-3 h-3 rounded-full border-2 border-marker-accent border-t-transparent animate-spin" />
                )}
                <span className="truncate">{t.first.question}</span>
              </div>
              <div className="text-xs text-marker-muted mt-0.5">
                {formatRelativeTime(t.latest.created_at)}
                {isThread && <span className="ml-1">· {t.n} turns</span>}
                {t.latest.error && <span className="ml-1 text-marker-bad">· error</span>}
              </div>
            </button>
          )
        })}
      </div>

      <div className="border-t border-marker-border p-3 text-xs text-marker-muted">
        Marker v0 · <span className="text-marker-text">Ask questions in plain English</span>
      </div>
    </div>
  )
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diff = (now - then) / 1000  // seconds
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
  return iso.slice(0, 10)
}
